import * as dateFns from 'date-fns';
import _ from 'lodash';
import { Stack, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import RecurringIcon from '@mui/icons-material/EventRepeatRounded';
import OneTimeIcon from '@mui/icons-material/EditCalendarRounded';
import ScenariosIcon from '@mui/icons-material/Extension';
import { allCategoryNames, categoryMap } from 'src/utils/catmap';
import {
  segmentForDate,
  segmentContainsDate,
  startOfRollupPeriod,
  endOfRollupPeriod,
  stepSizeForPeriod,
  divisorForPeriod,
} from 'src/utils/rollup';
import { isRetired } from 'src/utils/forecast';
import normalizeDate from 'src/utils/normalizeDate';
import { PREFER_CURRENT_BALANCE_OVER_AVAILABLE_BALANCE } from 'src/utils/constants';
import { patchesInSegments } from '../budget';

// filter accounts by type
export const getAccounts = (accounts, accountTypes) =>
  accounts.filter((a) => accountTypes.includes(a.type) || accountTypes.includes(a.subtype));

export const altPluralizeString = (word, count) => (count === 1 ? word : `of a ${word}`);
export const pluralizeString = (word, count, altFormat) =>
  altFormat && count < 1 ? altPluralizeString(word, count) : count === 1 ? word : `${word}s`;

// combine historical and forecasted data into a single dataset, splitIndex
export const mergeDataSets = (txByCategories, detailedForecast, segments, splitOffset = 0) => {
  const aggregateData = {};
  const splitIndex = getForecastingIndex(segments) + splitOffset;

  const categories = new Set([...Object.keys(txByCategories), ...Object.keys(detailedForecast)]);

  categories.forEach((detailedCategory) => {
    const newData = { ...valueForEntry(txByCategories[detailedCategory]) };

    segments.forEach((seg, index) => {
      if (index > splitIndex) {
        const forecast = detailedForecast[detailedCategory];
        if (forecast) {
          newData[seg.label] = valueForEntry(forecast)[seg.label] || 0;
        }
      }
    });

    aggregateData[detailedCategory] = { data: newData };
  });

  return aggregateData;
};

// Utility function to aggregate transactions for a category
export function segmentedDataForCategory(transactions, category) {
  const res = Object.keys(transactions ?? {}).reduce((sum, key) => {
    const value = transactions[key];
    if (key === category) {
      return sum + value;
    }
    return sum;
  }, 0);

  return res;
}

// Utility function to calculate weighted average

// ! This is a modified version of the weightedAverage function that ignores zero values until the first non-zero value
// ! This was used in the previous version of the weightedAverage function, but it was removed in favor of the current version
// ! because it was causing issues with the weighted average being too heavily influenced by the zero values
export function weightedAverage(nums) {
  const firstNonZeroIndex = nums?.findIndex((value) => value !== 0);

  if (firstNonZeroIndex === -1) {
    return 0; // Return 0 if all values are zero
  }

  const nonZeroValues = nums.slice(firstNonZeroIndex);
  const slantFactor = nonZeroValues.length;
  const totalWeights = nums.map((num, i) =>
    i >= firstNonZeroIndex ? i * slantFactor : 0.001 * slantFactor
  );

  const [sum, weightSum] = nums.reduce(
    (acc, num, index) => {
      acc[0] += num * totalWeights[index];
      acc[1] += totalWeights[index];
      return acc;
    },
    [0, 0]
  );

  return sum / weightSum || 0;
}

// export function weightedAverage(nums, options = {}) {
//   // Weight calculation parameters:
//   //
//   // baseWeight: Controls how much influence zero values have
//   // - Lower (0.1): Zeros barely affect average (good for irregular expenses)
//   // - Higher (0.5): Zeros significantly reduce average (good for regular bills)

//   // timeSlant: Controls recency bias in weighting
//   // - Lower (1.0): Historical patterns weighted evenly (good for stable expenses)
//   // - Higher (2.0): Recent values weighted heavily (good for changing patterns)

//   // valueSlant: Controls mode-clustering weight
//   // - Lower (1.0): All non-zero values weighted similarly (good for variable amounts)
//   // - Higher (2.0): Values near mode weighted heavily (good for consistent amounts)

//   // reverse: If true, reverses array so recent values are first
//   // - False: Older values get higher index (default)
//   // - True: Recent values get higher index (recommended for forecasting)

//   // Example with monthly income:
//   // Input:  [2000, 2000, 0,    2100, 0,    2000]  // Bi-monthly paycheck
//   // Weight: [1.0,  1.0,  0.1,  0.8,  0.1,  1.0]   // High weight on regular amount
//   // Result: ~2000 (weighted toward consistent paycheck amount)

//   const { baseWeight = 0.0001, timeSlant = 1.2, valueSlant = 1.8, reverse = false } = options;

//   const values = reverse ? [...nums].reverse() : nums;
//   const nonZeroValues = values.filter((v) => v !== 0);
//   if (nonZeroValues.length === 0) return 0;

//   // Find mode and analyze its pattern
//   const valueFrequency = nonZeroValues.reduce((acc, val) => {
//     acc[val] = (acc[val] || 0) + 1;
//     return acc;
//   }, {});
//   const [mode, modeFrequency] = Object.entries(valueFrequency).reduce((a, b) =>
//     a[1] > b[1] ? a : b
//   );

//   // Calculate pattern regularity
//   const expectedMonthlyOccurrences = nonZeroValues.length / values.length;
//   const patternRegularity = modeFrequency / nonZeroValues.length; // How consistent are the non-zero values?

//   const weights = values.map((value, index) => {
//     if (value === 0) {
//       // Zero values get baseWeight, adjusted down if pattern is regular
//       return baseWeight * (1 - patternRegularity);
//     }

//     // 1. Time-based weight: more recent values get higher weight
//     const timeWeight = ((index + 1) / values.length) ** timeSlant;

//     // 2. Value-based weight: values closer to mode get higher weight
//     const valueWeight = (1 / (Math.abs(value - mode) / Math.abs(mode) + 1)) ** valueSlant;

//     // 3. Pattern-based weight: boost weight if value matches a regular pattern
//     const patternWeight = value === Number(mode) ? 1 + patternRegularity : 1;

//     return timeWeight * valueWeight * patternWeight;
//   });

//   // Calculate weighted average
//   const [sum, weightSum] = values.reduce(
//     (acc, num, index) => {
//       acc[0] += num * weights[index];
//       acc[1] += weights[index];
//       return acc;
//     },
//     [0, 0]
//   );

//   // Adjust final average by how often transactions occur
//   return (sum / weightSum) * expectedMonthlyOccurrences || 0;
// }

export function budgetSnapshotsByCategories(period, budgetSnapshots, segments, options) {
  const { invert = false, filter = () => true, patches = [] } = options;

  // Create a mapping of primary categories to their data
  const categoryDataMap = {};

  const dates = segments.map((e) => e.value);
  const initialForecastingIndex = getForecastingIndex(segments);

  // for each budget snapshot, add the value to the appropriate segment
  budgetSnapshots.forEach((budget) => {
    const timestampDate = normalizeDate(budget.timestamp);
    const segmentIdx = segmentForDate(period, timestampDate, dates);
    const segment = segments[segmentIdx];

    if (segmentIdx >= 0) {
      // Process base budget categories
      Object.entries(budget.categories).forEach(([detailedCategory, value]) => {
        if (detailedCategory && filter(categoryMap(detailedCategory)?.primaryKey)) {
          // Initialize category data if needed
          if (!categoryDataMap[detailedCategory]) {
            categoryDataMap[detailedCategory] = {
              data: Object.fromEntries(segments.map(({ label }) => [label, 0])),
            };
          }

          if (segmentIdx <= initialForecastingIndex) {
            categoryDataMap[detailedCategory].data[segment.label] += invert ? -value : value;
          }
        }
      });
    }
  });

  // Process patches from the budgets array
  patches.forEach((budget) => {
    if (!budget.patches?.length) return;

    const timestampDate = normalizeDate(budget.timestamp);
    const segmentIdx = segmentForDate(period, timestampDate, dates);
    const segment = segments[segmentIdx];

    if (segmentIdx >= 0) {
      budget.patches.forEach((patch) => {
        Object.entries(patch.categories ?? {}).forEach(([detailedCategory, patchData]) => {
          if (detailedCategory && filter(categoryMap(detailedCategory)?.primaryKey)) {
            // Initialize category data if needed
            if (!categoryDataMap[detailedCategory]) {
              categoryDataMap[detailedCategory] = {
                data: Object.fromEntries(segments.map(({ label }) => [label, 0])),
              };
            }

            // Add patch amount to the segment
            const patchAmount = Math.abs(patchData.amount ?? 0);
            if (segmentIdx <= initialForecastingIndex) {
              categoryDataMap[detailedCategory].data[segment.label] += invert
                ? patchAmount
                : patchAmount;
            }
          }
        });
      });
    }
  });

  return categoryDataMap;
}

const invertValue = (invert, value) => (invert ? -value : value);

// Refactored transactionsByCategories function
export const transactionsByCategories = (rollup, segments, shouldInvert = false, options) => {
  // Get all categories in the rollup, along with all detailed categories
  const rollupCategories = _.uniq(
    Object.values(rollup)
      .reduce((keys, item) => {
        if (item && typeof item === 'object') {
          keys = keys.concat(Object.keys(item));
        }
        return keys;
      }, [])
      .concat(allCategoryNames)
  ).filter((e) => options?.filter?.(e) ?? e);

  const consolidatedData = {};
  rollupCategories.forEach((cat) => {
    // retrieve data for the category by segment
    const dataBySegment = segments
      // .filter((s) => !s.future && !s.current)
      .reduce((result, segment) => {
        const segmentLabel = segment.label;
        const value = segmentedDataForCategory(rollup[segmentLabel], cat);
        const invertedValue = invertValue(shouldInvert, value);

        // Add the property to the result object
        result[segmentLabel] = invertedValue;

        return result;
      }, {});

    // Get complete periods for average calculation
    const completePeriodsData = segments
      .filter((s) => !s.future && !s.current && !s.incomplete)
      .map((segment) => dataBySegment[segment.label])
      .filter((value) => value !== undefined);

    if (!consolidatedData[cat]) {
      consolidatedData[cat] = {
        data: dataBySegment,
        average: weightedAverage(completePeriodsData, options),
      };
    }
  });

  return consolidatedData;
};

export const isCreditCardPayment = (cat) => ['LOAN_PAYMENTS_CREDIT_CARD_PAYMENT'].includes(cat);

export const accountBySegments = (accountId, period, snapshots, segments) => {
  const results = {};
  segments?.forEach((segment) => {
    const snaps = snapshotsInSegment(period, snapshots, segment);
    snaps?.forEach((snap) => {
      const res = {
        ...snap,
        ...snap?.accounts.find((acc) => acc.id === accountId),
      };

      delete res.accounts;
      results[segment.label] = [...(results[segment.label] ?? []), res];
    });
  });
  return results;
};

const isValidBalance = (val) =>
  val !== null && !Number.isNaN(val) && val !== undefined && val !== 0;

const getBalanceFromFields = (account, fields) => {
  const validField = fields.find((field) => isValidBalance(account?.[field]));
  return Math.abs(validField ? account[validField] : 0);
};

/*
  If PREFER_CURRENT_BALANCE_OVER_AVAILABLE_BALANCE is true, will prefer current balance over available balance when calculating/displaying account values 
  If false, will prefer available balance over current balance
*/
export const balanceForAccount = (account) => {
  const balanceFields = {
    investment: ['current_balance', 'value', 'amount'],
    banking: PREFER_CURRENT_BALANCE_OVER_AVAILABLE_BALANCE
      ? ['current_balance', 'available_balance', 'amount', 'value']
      : ['available_balance', 'current_balance', 'amount', 'value'],
    liability: ['outstanding_balance', 'current_balance', 'available_balance', 'amount', 'value'],
  };

  let balance = 0;

  // First get the raw balance based on account type
  if (
    account?.type === 'investment' ||
    account?.subtype === 'trust' ||
    account?.subtype === 'ira' ||
    account?.subtype === 'retirement'
  ) {
    balance = getBalanceFromFields(account, balanceFields.investment);
  } else if (isLiability(account)) {
    balance = Math.abs(account.current_balance ?? 0);
  } else if (
    account?.type === 'generic' ||
    account?.subtype === 'checking' ||
    account?.subtype === 'savings'
  ) {
    balance = getBalanceFromFields(account, balanceFields.banking);
  } else {
    balance = getBalanceFromFields(account, balanceFields.banking);
  }

  // Then apply the appropriate sign based on account type
  if (isLiability(account)) {
    balance = -getBalanceFromFields(account, balanceFields.liability);
  }

  return balance;
};

// Helper function to determine if an account is a liability
const isLiability = (account) => {
  const liabilityTypes = ['credit card', 'loan'];
  const liabilitySubtypes = [
    'credit card',
    'mortgage',
    'line of credit',
    'student',
    'auto',
    'personal',
    'home equity',
  ];

  return liabilityTypes.includes(account?.type) || liabilitySubtypes.includes(account?.subtype);
};

// check the liability (and/or it's account) for a value
export const valueForLiability = (liability, fallbackValue) => {
  const liabilityValue = balanceForAccount(liability);
  const accountValue = balanceForAccount(liability?.account);
  const altValue = isValidBalance(liabilityValue) ? liabilityValue : fallbackValue ?? 0;

  return Math.max(
    0,
    isValidBalance(liabilityValue)
      ? liabilityValue
      : isValidBalance(accountValue)
      ? accountValue
      : altValue
  );
};

export const isCheckingAccount = (account) => account?.subtype.includes('checking');

// Forecast the values for an account by segment, including budget patches
export const seriesForAccount = (account, period, segments, options) => {
  const data = {};
  const dividendPayments = {};

  const growthRate = growthRateForType(account, options);
  const baseValue = balanceForAccount(account);

  segments.forEach((s, index) => {
    if (s.future /* && !s.current */) {
      const initialValue = getTotalPatchBalanceForAccount(s, account.id, options?.patches, period);
      const previousValue = initialValue ?? data[segments[index - 1]?.label] ?? baseValue;

      // Calculate withdrawal first
      let currentValue = previousValue;

      if (accountPaysDividends(account)) {
        const _withdrawalRate = withdrawalRateForType(account, options, s.retired);
        const withdrawalRate = _withdrawalRate / divisorForPeriod(period);

        // Calculate withdrawal amount based on current value
        const retirementMonthlyWithdrawal = currentValue * withdrawalRate;
        const withdrawalAmount = retirementMonthlyWithdrawal || 0;

        // Track dividend payments
        dividendPayments[s.label] = withdrawalAmount;

        // Subtract withdrawal
        currentValue -= withdrawalAmount;

        if (s.label === 'Sep 31') {
          console.log(
            `${s.label} ${account.name}:`,
            `\nPrevious Value: ${previousValue}`,
            `\nWithdrawal Rate: ${_withdrawalRate} (${withdrawalRate})`,
            `\nWithdrawal Amount: ${withdrawalAmount}`,
            `\nValue After Withdrawal: ${currentValue}`,
            `\nGrowth Rate: ${growthRate}`,
            `\nFinal Value: ${currentValue * growthRate}`
          );
        }
      }

      // Apply growth after withdrawal
      currentValue *= growthRate;

      data[s.label] = fallThroughZero(currentValue, true);
    } else {
      data[s.label] = fallThroughZero(baseValue, true);
    }
  });

  // Add cashflow to bank accounts with period-appropriate aggregation
  const cumCashFlow = options?.cashflow?.rows?.find((e) => e.key === 'cumNetCashFlow')?.data;

  if (isBankAccount(account) && account.isPrimary && cumCashFlow) {
    // const periodMultiplier =
    //   {
    //     month: 1,
    //     quarter: 1 / 3, // Average monthly impact for quarter
    //     year: 1 / 12, // Average monthly impact for year
    //   }[period] || 1;

    Object.keys(data).forEach((key) => {
      // For longer periods, use the average monthly impact
      data[key] += cumCashFlow[key]; //* periodMultiplier;
    });

    console.log('Added cumulative cashflow to bank account', account.name, cumCashFlow);
  }

  return { data, dividendPayments };
};

/**
 * Calculates the total value of all patches applied to an account for a given segment
 *
 * @param {Object} segment - The time segment to check for patches
 * @param {string} accountId - The ID of the account to find patches for
 * @param {Array} patches - Array of budget patches that might affect this account
 * @param {Array} period - The period to check for patches
 * @returns {number|null} The total patch value, or null if no patches found
 *
 * Example:
 * If account "123" has two patches in March:
 * patch1: { amount: 100 }
 * patch2: { amount: 200 }
 * Returns: 300
 */
const getTotalPatchBalanceForAccount = (segment, accountId, patches, period) => {
  // Find patches for this segment
  const segmentPatches = patchesInSegments([segment], period, patches);
  if (!segmentPatches) return null;

  // Find patch that affects this account
  const relevantPatch = segmentPatches.find((patch) =>
    patch.patches?.some((p) => p.categories[accountId])
  );
  if (!relevantPatch) return null;

  // Sum up all patch amounts for this account
  return relevantPatch.patches.reduce(
    (total, patch) => total + (balanceForAccount(patch.categories[accountId]) ?? 0),
    0
  );
};

// determines if the value can fall through to 0
const fallThroughZero = (value, canFall = false) => (canFall ? value : Math.max(value, 0));

function snapshotsInSegment(period, snapshots, segment) {
  return snapshots.filter((snap) =>
    segmentContainsDate(period, segment, normalizeDate(snap.timestamp))
  );
}

export const getForecastingIndex = (segments) => {
  let initialForecastingIndex = segments.findIndex((s) => s.current);
  if (initialForecastingIndex < 0) initialForecastingIndex = segments.length;
  return initialForecastingIndex;
};

export const getRetirementIndex = (segments) => {
  let initialRetirementIndex = segments.findIndex((s) => s.retired);
  if (initialRetirementIndex < 0) initialRetirementIndex = segments.length;
  return initialRetirementIndex;
};

/**
 * Calculate the growth rate based on the inflation rate and step size.
 *
 * @param {Object} inflationRateObj - Inflation rate information.
 * @param {String} key - Category key.
 * @param {Number} annualInflationRate - Annual inflation rate.
 *
 * @return {Number} Growth rate.
 */
function computeGrowthRate(inflationRateObj, annualInflationRate) {
  const { stepSize, offsetIndex } = inflationRateObj;
  if (stepSize === 0 || offsetIndex === 0) return 1; // apply no inflation if the step is 0

  const applyInflation = offsetIndex % stepSize === 0;
  if (applyInflation) {
    // Change from linear to compound growth
    inflationRateObj.rate = annualInflationRate ** (inflationRateObj.offsetIndex / stepSize);
  }

  return inflationRateObj.rate;
}

export const columnContainsBudgetPatch = (segment, budgets, period) => {
  const { value: date } = segment;
  if (!date) return null;
  const startDate = startOfRollupPeriod(date, period);
  const endDate = endOfRollupPeriod(date, period);

  const results = budgets
    ?.filter((budget) =>
      dateFns.isWithinInterval(budget.timestamp, {
        start: startDate,
        end: endDate,
      })
    )
    ?.flatMap((e) => e.patches)
    .filter((e) => !_.isEmpty(e));

  return results;
};

export const renderCell = ({
  period,
  budgets,
  segment,
  params,
  nameForCategory = (cat) => categoryMap(cat)?.primary,
}) => {
  // const period = 'month';
  const theme = useTheme();
  const tableGroup = params?.rowNode?.groupingKey;
  const patches = columnContainsBudgetPatch(segment, budgets, period);

  const categoriesInPatches = patches?.map((e) => Object.keys(e?.categories ?? {}))?.flat();
  const parentCategoriesInPatches = categoriesInPatches?.map(nameForCategory);
  const accountCategoriesInPatches = params?.rowNode?.children?.some((e) =>
    categoriesInPatches.includes(e)
  );

  const isPatched = patches ? categoriesInPatches?.includes(tableGroup) : false;
  const containsPatch =
    parentCategoriesInPatches?.includes(tableGroup) || accountCategoriesInPatches;
  const categoryPatches = patches?.filter((p) => p.categories[tableGroup]);
  const isScenarioBudgetPatch = !_.isEmpty(categoryPatches?.filter((p) => p?.scenario));

  const isRecurring = categoryPatches?.some((p) => p?.categories[tableGroup]?.type === 'recurring');
  const isOneTime = categoryPatches?.some((p) => p?.categories[tableGroup]?.type === 'onetime');

  // if (categoriesInPatches?.includes(tableGroup))
  //   console.log({
  //     isPatched,
  //     containsPatch,
  //     categoryPatches,
  //     isScenarioBudgetPatch,
  //     isRecurring,
  //     isOneTime,
  //   });

  const isConflict =
    patches?.some((p) => p.categories[tableGroup] && p.scenario) &&
    patches?.some((p) => p.categories[tableGroup] && !p.scenario);

  // const isMixedPatchGroup = patches?.some((p) => _.isEmpty(p.scenario)) && patches?.some((p) => !_.isEmpty(p.scenario));

  const ratio = isScenarioBudgetPatch ? 1 : 0;

  const patchTextColor = isConflict
    ? theme.palette.warning.main
    : interpolateColors(theme.palette.secondary.main, theme.palette.primary.main, ratio);

  const textOpacity = segment?.future ? 0.6 : 1;

  const colorForValue = (num) =>
    num < 0
      ? alpha(theme.palette.error.main, textOpacity)
      : segment?.future
      ? 'text.disabled'
      : 'inherit';

  const colorForPatch = (patched, childPatched) =>
    patched
      ? patchTextColor
      : childPatched
      ? alpha(theme.palette.secondary.main, 0.6)
      : colorForValue(params.value, true);

  const textColor = colorForPatch(isPatched, containsPatch); // !segment?.future ? : colorForValue(params.value);

  // const icon = isScenarioBudgetPatch ? 'mdi:puzzle' : isRecurring ? ""
  return (
    <Stack direction="row" justifyContent="end" alignItems="center" padding={0.5}>
      {/* <Iconify htmlColor={patchTextColor} icon={icon} fontSize="12px" sx={{ mar: 1 }} /> */}
      {isScenarioBudgetPatch && (
        <ScenariosIcon htmlColor={patchTextColor} fontSize="12px" sx={{ mr: 1 }} />
      )}
      {isRecurring && !isScenarioBudgetPatch && (
        <RecurringIcon htmlColor={patchTextColor} fontSize="12px" sx={{ mr: 1 }} />
      )}
      {isOneTime && !isScenarioBudgetPatch && (
        <OneTimeIcon htmlColor={patchTextColor} fontSize="12px" sx={{ mr: 1 }} />
      )}
      <Typography color={textColor} textAlign="end">
        {params.formattedValue}
      </Typography>
    </Stack>
  );
};

// Function to interpolate between two colors
const interpolateColors = (color1, color2, ratio) => {
  const hex = (x) => {
    x = x.toString(16);
    return x.length === 1 ? `0${x}` : x;
  };

  const r = Math.ceil(
    parseInt(color1.substring(1, 3), 16) * (1 - ratio) +
      parseInt(color2.substring(1, 3), 16) * ratio
  );
  const g = Math.ceil(
    parseInt(color1.substring(3, 5), 16) * (1 - ratio) +
      parseInt(color2.substring(3, 5), 16) * ratio
  );
  const b = Math.ceil(
    parseInt(color1.substring(5, 7), 16) * (1 - ratio) +
      parseInt(color2.substring(5, 7), 16) * ratio
  );

  return `#${hex(r)}${hex(g)}${hex(b)}`;
};

/**
 * Calculate the forecasted value based on applied patches and inflation rate.
 *
 * @param {Object} data - Data for calculating the forecasted value.
 * @param {Map} appliedPatches - Map to store applied patches.
 *
 * @return {Number} Forecasted value.
 */
export function forecastValue({
  appliedPatches,
  key,
  inflationRateObj,
  patchValues,
  inflation,
  options,
  segment,
}) {
  computeGrowthRate(inflationRateObj, inflation);
  const initialValue = getPatchedBudgetItem(
    patchValues,
    key,
    appliedPatches,
    inflationRateObj,
    options,
    segment
  );

  inflationRateObj.offsetIndex++;
  return initialValue * inflationRateObj.rate;
}

/**
 * Get the budget item value with applied patches.
 *

 * @param {Object} patchValues - Patch values for the category.
 * @param {String} key - Category key.
 * @param {Map} appliedPatches - Map to store applied patches.
 * @param {Object} inflationRateObj - Inflation rate information.
 *
 * @return {Number} Initial value with patches applied.
 */
function getPatchedBudgetItem(
  patchValues,
  key,
  appliedPatches,
  inflationRateObj,
  options,
  segment
) {
  const { hasPatchValue, onetimePatch, budget, shouldApplyOneTimePatch } = patchValues;

  // GET THE PATCH FOR THE CATEGORY
  const patch = budget?.patches?.find((e) => e.categories && e.categories[key] !== undefined)
    ?.categories?.[key];

  if (hasPatchValue && !onetimePatch) {
    const patchKey = `${budget.patches[0].id}-${key}`;
    const patchAppliedBefore = appliedPatches.get(patchKey);

    if (patch?.growth !== undefined) {
      inflationRateObj.stepSize = stepSizeForPeriod(patch?.growth); // Steps between applying inflation (default is monthly)
    }

    if (!patchAppliedBefore) {
      appliedPatches.set(patchKey, true);
      inflationRateObj.offsetIndex = 0;
      inflationRateObj.rate = 1;
    }
  }

  // Reset the inflation rate upon applying a patch
  if (shouldApplyOneTimePatch) {
    inflationRateObj.rate = 1;
  }

  const initialValue = shouldApplyOneTimePatch
    ? budget?.patches.reduce((acc, cur) => acc + (cur.categories[key]?.amount ?? 0), 0)
    : (options?.fallbackToZero || false) && !hasPatchValue
    ? 0
    : budget.categories[key] ?? 0;

  //--------------------------------
  // ACCOUNT VARIABLES FOR MANUAL FORECASTING
  //--------------------------------
  // const brokerageAccounts = options?.assets?.rows?.filter(accountPaysDividends);
  // const retirementAccounts = brokerageAccounts?.filter(isRetirementAccount);
  // const nonRetirementAccounts = brokerageAccounts?.filter((a) => !isRetirementAccount(a));

  // // determine the type of account-based forecasting we will perform
  // const manualForecastCategories = ['INCOME_DIVIDENDS', 'INCOME_RETIREMENT'];
  // const forecastingDividends = key === 'INCOME_DIVIDENDS';

  // // early return for account-based forecasting
  // if (manualForecastCategories.includes(key)) {
  //   return calculateIncomeForAccounts(
  //     segment.label,
  //     true,
  //     forecastingDividends ? nonRetirementAccounts : retirementAccounts,
  //     initialValue || 0,
  //     false, // skip taxation
  //     options?.taxRateRetention
  //   );
  // }

  return initialValue;
}

/**
 * Find the budget for a specific segment date.
 * @param {Date} segmentDate - The date of the segment.
 * @param {Array} orderedBudgets - Ordered budget data.
 * @param {Array} budgets - Budget data.
 * @returns {Object} - The budget data for the segment date.
 */
export function findBudgetForSegment(segmentDate, orderedBudgets, budgets) {
  return (
    orderedBudgets.find((b) => segmentDate >= dateFns.startOfMonth(new Date(b.timestamp))) ||
    budgets[0]
  );
}

export const valueForEntry = (entry) => entry?.data ?? {};

/**
 * Determine patch-related values.
 * @param {Date} segmentDate - The date of the segment.
 * @param {Object} budget - The budget data.
 * @param {string} key - The key to identify the category.
 * @param {string} period - The growth period.
 * @returns {Object} - An object containing patch-related values.
 */
export function determinePatchValues(segmentDate, budget, key, period) {
  const hasPatchValue = budget?.patches && budget.patches.some((e) => e.categories[key]);
  const onetimePatch =
    hasPatchValue && budget?.patches.some((e) => e.categories[key]?.type === 'onetime');
  const shouldApplyOneTimePatch =
    onetimePatch &&
    startOfRollupPeriod(new Date(budget?.timestamp), period).getTime() === segmentDate.getTime();
  return { hasPatchValue, onetimePatch, shouldApplyOneTimePatch, budget };
}

export const splice = (a = {}, b = {}, segments = [], offset = 0, showCurrent = true) => {
  const results = {};

  const splitIndex = getForecastingIndex(segments) + offset;
  const categories = new Set([...Object.keys(a), ...Object.keys(b)]);

  categories.forEach((detailedCategory) => {
    const newData = { ...valueForEntry(a[detailedCategory]) };

    segments.forEach((seg, index) => {
      if (seg.current) newData.current = valueForEntry(a[detailedCategory])[seg.label];

      if (seg.current && showCurrent) {
        newData[seg.label] = newData.current || 0;
      } else if (index >= splitIndex && showCurrent) {
        const forecast = valueForEntry(b[detailedCategory])[seg.label];
        if (forecast) newData[seg.label] = forecast || 0;
      } else {
        const forecast = valueForEntry(b[detailedCategory])[seg.label];
        if (forecast) newData[seg.label] = forecast || 0;
      }

      newData[seg.label] = nanGuard(newData[seg.label]);
    });

    results[detailedCategory] = { data: newData };
  });

  return results;
};

export function convertToPrimaryCategories(dataset) {
  const unmappedCategories = new Set();

  const categories = Object.entries(dataset).reduce((results, [category, { data }]) => {
    if (typeof category === 'string') {
      const { primary } = categoryMap(category) ?? {};

      // Log categories that don't map to a primary category
      if (!primary) unmappedCategories.add(category);

      if (primary) {
        results[primary] = results[primary] || { data: {} };

        Object.entries(data).forEach(([month, value]) => {
          results[primary].data[month] = (valueForEntry(results[primary])[month] || 0) + value;
        });
      }
    }

    return results;
  }, {});

  // Log any unmapped categories
  if (unmappedCategories.size > 0)
    console.warn('Categories without primary mapping:', Array.from(unmappedCategories));

  return categories;
}

export function sumDatasetByColumn(dataset) {
  const monthlyTotals = {};

  if (!dataset) return monthlyTotals;

  Object.entries(dataset).forEach(([, entry]) => {
    const data = entry?.data ?? entry;

    if (!Array.isArray(data)) {
      Object.entries(data).forEach(([month, value]) => {
        const positiveValue = value < 0 ? 0 : value;
        monthlyTotals[month] = (monthlyTotals[month] || 0) + positiveValue;
      });
    }
  });

  return monthlyTotals;
}

export function maxSpendingForDatasets(...datasets) {
  // Calculate monthly totals for each dataset independently
  const allTotals = datasets.map((dataset) => {
    const totals = sumDatasetByColumn(dataset);
    return Object.values(totals);
  });

  // Flatten all totals into a single array and find the maximum
  const allValues = allTotals.flat();

  return Math.max(...allValues, 0);
}

// determine the tax rate for a category
export const taxRateForCategory = (category, options) => {
  let taxation = 1;

  if (
    ['INCOME_DIVIDENDS', 'INCOME_1099', 'SOCIAL_SECURITY', 'INCOME_RETIREMENT'].includes(category)
  ) {
    taxation = options?.taxRateRetention ?? 1;
  }

  return taxation;
};

/**
 * Forecasts spending based on provided series, segments, budgets, and period.
 *
 * @param {Object} categories - Collection of data series.
 * @param {Array} segments - Time segments for forecasting.
 * @param {Array} budgets - Budget data for different periods.
 * @param {String} period - The period for the forecast.
 *
 * @return {Array} Forecasted spending data.
 */
export function forecastCategories(categories, segments, budgets, period, inflation, options) {
  const orderedBudgets = [...budgets].reverse();
  const appliedPatches = new Map();

  // Forecast the data for a single category
  const forecastCategory = ([category]) => {
    // Initialize as an inflation object, with offset index and rate of growth via step size
    const inflationRateObj = { offsetIndex: 0, rate: inflation, stepSize: 1 };

    // Forecast the data for each segment
    const data = forecastSegments(segments, (key, segment) => {
      // this ensures that wages are excluded when the user retires
      const exclude_wages_while_retired = !(segment.retired && category.includes('WAGES'));

      const segmentDate = dateFns.startOfMonth(new Date(segment.value));
      const budget = findBudgetForSegment(segmentDate, orderedBudgets, budgets);
      const patchValues = determinePatchValues(segmentDate, budget, category, period);

      // Apply if:
      // 1. Segment is current or future (for forecasting), OR
      // 2. Segment has patches (regardless of whether past/current/future)
      const cellContainsPatch = columnContainsBudgetPatch(segment, budgets, period)?.length > 0;
      const apply = segment.future || segment.current || cellContainsPatch;

      if (apply && exclude_wages_while_retired) {
        const forecastInfo = { patchValues, key: category, inflationRateObj, inflation };
        return forecastValue({ ...forecastInfo, appliedPatches, options, segment });
      }

      return 0;
    });

    return [category, { data }];
  };

  // Iterate over the categories (rebuilding them into an object)
  const results = Object.fromEntries(Object.entries(categories).map(forecastCategory));

  // Add additional income sources
  const includesIncomeCategories = Object.keys(categories).some((category) =>
    category.includes('INCOME')
  );
  const includesRetiredSegments = segments.some(({ retired }) => retired);

  // whether to manually add social security income
  const addSocialSecurity =
    /// if we're forecasting income
    includesIncomeCategories &&
    /// if the user is retired in any of the segments
    includesRetiredSegments &&
    /// if the user is not already receiving SS
    !isRetired(options?.birthdate, new Date(), options?.targetRetirementAge);

  return {
    ...results,
    ...(addSocialSecurity
      ? {
          SOCIAL_SECURITY: socialSecurityValues({ ...options, segments }),
        }
      : {}),
  };
}

// calculate income based on the account type
const calculateIncome = (account, value, applyTaxationIfNeeded, taxRateRetention) => {
  let taxation = 1;

  if (isRetirementAccount(account) && applyTaxationIfNeeded) {
    taxation = taxRateRetention ?? 1;
  }

  return value * taxation;
};

// calculate income from accounts based on dividends
export const calculateIncomeForAccounts = (
  key,
  future,
  accounts,
  baseIncome = 0,
  applyTaxationIfNeeded,
  taxRateRetention
) => {
  // if we are not forecasting, return the original value
  if (!future) return baseIncome;

  // calculate income based on the account type
  return accounts.reduce((total, account) => {
    const value = account?.dividendPayments?.[key] ?? 0;

    return total + calculateIncome(account, value, applyTaxationIfNeeded, taxRateRetention);
  }, baseIncome);
};

export function socialSecurityValues({
  segments,
  averageIncomeGrowthRate,
  socialSecurityIncome,
  ...options
}) {
  const initialRetirementIndex = segments.findIndex((e) => e.retired === true);
  const data = {};

  segments.forEach((s, index) => {
    if (s.retired) {
      const offsetIndex = index - initialRetirementIndex;
      const growth = 1 + (averageIncomeGrowthRate - 1) * offsetIndex;
      data[s.label] = (socialSecurityIncome || 0) * growth;
    } else data[s.label] = null;
  });

  return { data };
}

export function calculateFullRangeData(
  segments = [],
  aggregateData,
  type,
  usePostTaxValue = false,
  options
) {
  const results = {};

  segments.forEach((segment) => {
    const processedData = Object.entries(aggregateData ?? {}).map(([category, { data }]) => {
      const taxation = taxRateForCategory(category, options);

      // Apply taxation if usePostTaxValue is true
      const processedValues = usePostTaxValue
        ? Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
              key,
              (type === 'spending' ? -1 : 1) * (value * taxation),
            ])
          )
        : data;

      // if (segment.label === 'Jan 25' && category.includes('INCOME') && usePostTaxValue) {
      //   console.log(
      //     `${segment.label} [${category}] rate: ${taxation}, pre: ${data[segment.label]}, post: ${
      //       processedValues[segment.label]
      //     }`
      //   );
      // }

      return {
        category,
        data: processedValues,
      };
    });

    // Now reduce the processed data to get the total for the specified segment
    const total = processedData.reduce((acc, { data }) => acc + (data?.[segment.label] ?? 0), 0);

    results[segment.label] = total;
  });

  return Object.values(results);
}

export const nameForSegment = (segment, budgets, period) =>
  `${segment.label}${columnContainsBudgetPatch(segment, budgets, period)?.length > 0 ? '*' : ''}`;

// Domain category for account
/// https://plaid.com/docs/api/products/investments/
export function categoryForAccount(account) {
  const type = account.type || '';
  const subtype = account.subtype || '';
  const investmentAccountTypes = [
    'investment',
    'brokerage',
    'retirement',
    'real_estate',
    'private_asset',
  ];

  // Check if either type or subtype matches investment categories
  if (investmentAccountTypes.includes(type) || investmentAccountTypes.includes(subtype)) {
    if (isRetirementAccount(account)) return 'Retirement';
    return 'Investments';
  }

  // Check if either type or subtype is loan
  if (type === 'loan' || subtype === 'loan') {
    return 'Loan';
  }

  return 'Banking';
}

export const isBankAccount = (account) => categoryForAccount(account) === 'Banking';
export const getRetirementAccounts = (accounts) => accounts?.filter(isRetirementAccount) ?? [];
export const isRetirementAccount = (account) =>
  retirementAccountTypes.includes(account?.subtype) ||
  retirementAccountTypes.includes(account?.type);
// List of retirement account types
const retirementAccountTypes = [
  '401a',
  '401k',
  '403B',
  '457b',
  '529',
  'ira',
  'lif',
  'lira',
  'lrif',
  'lrsp',
  'pension',
  'profit sharing plan',
  'retirement',
  'roth',
  'roth 401k',
  'rrsp',
  'sep ira',
  'simple ira',
  'sipp',
  'stock plan',
  'thrift savings plan',
  'tfsa',
  // additional types
  'retirement_account',
];

export const isRetirementAccountTaxed = (account) => !account?.subtype?.includes('roth');

// !This is incomplete
// TODO: Parse account (sub)types
export const growthRateForType = ({ type, subtype }, scenario) => {
  const { averageEquityGrowthRate, averagePrivateAssetGrowthRate, averageRealEstateGrowthRate } =
    scenario;

  const brokerages = ['investment', 'retirement', 'brokerage'];
  const keys = [type, subtype];

  if (keys.includes('real_estate')) return averageRealEstateGrowthRate;
  if (keys.some((key) => brokerages.includes(key))) return averageEquityGrowthRate;

  return averagePrivateAssetGrowthRate;
};

export const withdrawalRateForType = (account, scenario, retired) => {
  const { averageEquityWithdrawalRate, averageRetirementEquityWithdrawalRate } = scenario;
  if (isRetirementAccount(account)) return retired ? averageRetirementEquityWithdrawalRate : 0;
  return averageEquityWithdrawalRate;
};

export function accountPaysDividends(account) {
  const { primaryCategory, detailedCategory, type, subtype } = account ?? {};
  return (
    primaryCategory === 'Retirement' ||
    detailedCategory === 'Brokerage' ||
    type === 'brokerage' ||
    type === 'investment' ||
    subtype === 'retirement_account'
  );
}

/*
// Function to calculate total spending per index
export function calculateTotalSpending(arrayOfObjects) {
  if (arrayOfObjects.length === 0) {
    return []; // Return an empty array if there are no objects
  }

  const dataLength = arrayOfObjects[0].data.length; // Get the length of the "data" array
  const totalSpending = new Array(dataLength).fill(0); // Initialize an array with zeros

  arrayOfObjects.forEach((obj) => {
    if (obj.data.length === dataLength) {
      obj.data.forEach((value, index) => {
        // Check if the value is numeric (not NaN)
        if (!Number.isNaN(value)) {
          totalSpending[index] += parseFloat(value); // Add the numeric value to the corresponding index
        }
      });
    }
  });

  return totalSpending;
}
*/

// export const transactionsByPrimaryCategories = (cats, segments) => {
//   const primaryCategories = resolveToDomainCategories(cats);
//   const consolidatedData = {};

//   Object.keys(primaryCategories).forEach((primaryCategory) => {
//     const { categories } = primaryCategories[primaryCategory];

//     const segmentData = segments.map((segment) =>
//       Object.keys(categories)
//         .map((detailedCategory) => {
//           const { data } = categories[detailedCategory];
//           return data[segment.label];
//         })
//         .reduce((acc, cur) => acc + cur, 0)
//     );

//     if (!consolidatedData[primaryCategory]) {
//       consolidatedData[primaryCategory] = {
//         name: primaryCategory,
//         data: segmentData,
//       };
//     } else {
//       consolidatedData[primaryCategory].data = consolidatedData[primaryCategory].data.map(
//         (value, index) => (parseFloat(value) + parseFloat(segmentData[index])).toFixed(2)
//       );
//     }
//   });

//   return Object.values(consolidatedData);
// };

/*
export const rollupToPrimaryCategory = (forecastedSeries) => {
  const consolidatedData = {};

  forecastedSeries.forEach((detailedCategory) => {
    const primaryCategory = categoryMap(detailedCategory.name, 'key')?.primary ?? 'Other';

    if (!consolidatedData[primaryCategory]) {
      consolidatedData[primaryCategory] = {
        name: primaryCategory,
        data: detailedCategory.data,
      };
    } else {
      consolidatedData[primaryCategory].data = consolidatedData[primaryCategory].data.map(
        (value, index) => parseFloat(value) + parseFloat(detailedCategory.data[index])
      );
    }
  });

  return Object.values(consolidatedData);
};
*/
/*
// convert from the Plaid categories, to Grow Bread categories (primary)
export const resolveToDomainCategories = (categories) => {
  const newCategories = {};

  Object.entries(categories)?.forEach(([category, { data }]) => {
    const domainCategory = categoryMap(category)?.primary; // display name of domain category

    if (!newCategories[domainCategory]) {
      newCategories[domainCategory] = { categories: { [category]: data } };
    } else {
      const prev = newCategories[domainCategory];
      newCategories[domainCategory] = { categories: { ...prev.categories, [category]: data } };
    }
  });

  return newCategories;
};
*/

// function sumArrayObjectsByCategory(data) {
//   return Object.values(
//     data.reduce((result, currentItem) => {
//       const { name, data: dataArray } = currentItem;
//       const primaryKey = categoryMap(name)?.primary;

//       if (primaryKey) {
//         if (!result[primaryKey]) {
//           result[primaryKey] = { name: primaryKey, data: dataArray };
//         } else {
//           result[primaryKey].data = result[primaryKey].data.map(
//             (value, index) => value + dataArray[index]
//           );
//         }
//       }

//       return result;
//     }, {})
//   );
// }

/* function filterTransactionsByDate(transactions, dateRange) {
  // Ensure that dateRange.startDate and dateRange.endDate are valid Date objects
  if (!(dateRange?.start instanceof Date) || !(dateRange?.end instanceof Date)) {
    // throw new Error('Invalid date range');
    return [];
  }

  // Use the filter method to filter transactions based on the date range
  const filteredTransactions = transactions.filter((transaction) => {
    const transactionDate = new Date(transaction.date);

    // Check if the transaction date is greater than startDate and less than endDate
    return transactionDate > dateRange.start && transactionDate < dateRange.end;
  });

  return filteredTransactions;
} */

export function flattenObject(obj) {
  const result = {};

  Object.entries(obj).forEach(([key, val]) => {
    // if object (and not null!), merge it with the resulting object,
    // otherwise just copy the value
    if (val && typeof val === 'object') Object.assign(result, val);
    else result[key] = val;
  });

  return result;
}

/*
const objectMap = (obj, func) =>
  Object.fromEntries(
    Object.entries(obj ?? {}).map(([k, v]) => [k, v === Object(v) ? objectMap(v, func) : func(v)])
  );
*/

export const forecastSegments = (segments, forecastFn) =>
  Object.fromEntries(
    segments.map((seg, index) => [seg.label, forecastFn(seg.label, { ...seg, index })])
  );

export const nanGuard = (value) => (Number.isNaN(value) ? 0 : value);
